import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import {
  markNotificationReadLocal,
  markAllNotificationsReadLocal,
} from "@redux/slices/oaapSlice";
import { markNotificationReadApi, markAllNotificationsReadApi } from "@services/oaap/notificationsApi";
import { markNotificationReadApi as markJcNotificationReadApi, markAllNotificationsReadApi as markAllJcNotificationsReadApi } from "@services/jcReview/notificationsApi";

const MODULE_ROUTE = { oaap: "/oaap", jc: "/jc-review" };

const TYPE_ICON = {
  feedback_received:    "📋",
  upload_pending_review:"📥",
  review_pending:       "🔔",
  compliance_overdue:   "🚨",
  compliance_due_soon:  "⏰",
  compliance:           "📅",
};

/**
 * NotificationDrawer
 *
 * Application-wide right-side slide-in notification panel.
 * Show/hide is controlled by the `open` + `onClose` props.
 */
const NotificationDrawer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userCode = useSelector((state) => state?.userDetails?.details?.Id || "");
  const { notifications, notificationsLoading } = useSelector((s) => s.oaap);

  const unread = notifications.filter((n) => !n.is_read);

  const handleClick = async (n) => {
    // Mark as read
    if (!n.is_read) {
      dispatch(markNotificationReadLocal(n.id));
      try {
        const markRead = n.module === "jc" ? markJcNotificationReadApi : markNotificationReadApi;
        await markRead(n.id, userCode);
      } catch (_) {}
    }
    // Navigate to the page matching this notification's feature (defaults to OAAP for legacy rows)
    navigate(MODULE_ROUTE[n.module] || "/oaap");
    onClose();
  };

  const handleMarkRead = async (id, moduleName) => {
    dispatch(markNotificationReadLocal(id));
    try {
      const markRead = moduleName === "jc" ? markJcNotificationReadApi : markNotificationReadApi;
      await markRead(id, userCode);
    } catch (_) {}
  };

  const handleMarkAll = async () => {
    dispatch(markAllNotificationsReadLocal());
    try {
      await Promise.all([
        markAllNotificationsReadApi(userCode),
        markAllJcNotificationsReadApi(userCode),
      ]);
    } catch (_) {}
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 320 } }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.3)" } } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          backgroundColor: "#3901ff",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <NotificationsNoneOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
            Notifications
          </Typography>
          {unread.length > 0 && (
            <Chip
              label={unread.length}
              size="small"
              sx={{
                backgroundColor: "#ff3b30",
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.6rem",
                height: 18,
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {unread.length > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton onClick={handleMarkAll} size="small" sx={{ color: "#fff", opacity: 0.85, "&:hover": { opacity: 1, backgroundColor: "rgba(255,255,255,0.15)" } }}>
                <DoneAllOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff", "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" } }}>
            <CloseOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {notificationsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress size={28} sx={{ color: "#3901ff" }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 1 }}>
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 40, color: "#d0d0d0" }} />
            <Typography sx={{ fontSize: "0.8rem", color: "#808080" }}>No notifications</Typography>
          </Box>
        ) : unread.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 1 }}>
            <NotificationsNoneOutlinedIcon sx={{ fontSize: 40, color: "#d0d0d0" }} />
            <Typography sx={{ fontSize: "0.85rem", color: "#444", fontWeight: 600 }}>You're all up to date</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#999", textAlign: "center", px: 2 }}>No new notifications at this time.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {unread.map((n, idx) => (
              <React.Fragment key={n.id}>
                <ListItemButton
                  alignItems="flex-start"
                  onClick={() => handleClick(n)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    backgroundColor: "#f6f4ff",
                    "&:hover": { backgroundColor: "#ede9ff" },
                    gap: 1.5,
                  }}
                >
                  <Typography component="span" sx={{ fontSize: "1.1rem", lineHeight: 1, pt: 0.25, flexShrink: 0 }}>
                    {TYPE_ICON[n.type] || "📣"}
                  </Typography>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: "0.78rem", color: "#2b2b2b", lineHeight: 1.45, fontWeight: 600 }}>
                        {n.message}
                      </Typography>
                    }
                    secondary={
                      n.created_at ? (
                        <Typography component="span" sx={{ fontSize: "0.62rem", color: "#808080" }}>
                          {new Date(n.created_at).toLocaleString("en-IN", {
                            day: "2-digit", month: "short",
                            hour: "2-digit", minute: "2-digit",
                          })}
                        </Typography>
                      ) : null
                    }
                    disableTypography
                  />
                  <Box
                    sx={{
                      width: 8, height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#3901ff",
                      flexShrink: 0,
                      mt: 0.75,
                    }}
                  />
                </ListItemButton>
                {idx < unread.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

export default NotificationDrawer;
